import React, { FC, useState, useEffect } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { useAtom } from "jotai";
import Icon from "../../../../Icon";
import RText from "../../../../RText/RText";
import {
  showOddsCheckerOnRacecardAtom,
  showTrackerOnRacecardAtom,
  showFormOnRacecardAtom,
  showTimeformOnRacecardAtom,
} from "../../../../../atoms";
import { useResponsive } from "../../../../../hooks/useResponsive";
import variables from "../../../../../styles/variables";
import { Odd, Runner } from "../../../../../types/races/runner";
import { TrackedHorseComment } from "../../../Tracker/components/TrackedHorseComment";
import { HorseFormTable } from "../../../More/Horse/components/FormTable";
import { getHorseProfile } from "../../../../../client/more/horse";
import {
  Performance,
  HorseTrackerType,
} from "../../../../../types/races/horse";
import SquarePriceButton from "../../../../Buttons/SquarePriceButton";
import GridLayout from "../../../../Layout/GridLayout/GridLayout";
import { PriceFormat } from "../../../../../types/tipster";
import { formatCountryCode } from "../../../../../utils/race";
import TimeformIcon from "../../../../Icon/TimeformIcon";
import { sortOddsByPriority } from "../../../../../client/odds";
import { OddBookmakers } from "../../../../../types/bookmaker";
import RScrollView from "../../../../RScrollView";
import Gap from "../../../../Gap";

type Props = {
  runner: Runner;
  priceFormat: PriceFormat;
  displaysPremiumData: boolean;
  bookmakers: OddBookmakers;
  refreshListOfTrackedHorses: Function;
  trackedHorse?: HorseTrackerType;
};

/**
 * @file ExpandedRacecardRunnerRow.tsx
 * @description Displays detailed horse runner information in the racecard, including form, tracker notes, odds, and premium data like Timeform.
 * Displays an expanded view of a horse runner on the racecard,
 * including form history, tracking controls, odds, and premium data.
 *
 * @param {Runner} runner - The horse runner data to display.
 * @param {PriceFormat} priceFormat - The selected format for displaying odds (fractional, decimal, etc.).
 * @param {boolean} displaysPremiumData - Whether to show premium data like Timeform badges.
 * @param {OddBookmakers} bookmakers - The list of available bookmakers for odds display.
 * @param {Function} refreshListOfTrackedHorses - Callback to refresh the tracked horses list.
 * @param {HorseTrackerType} [trackedHorse] - The tracked horse data, if any.
 * @param {boolean} [isExpanded=false] - Whether the row is expanded to show more details.
 * @returns {JSX.Element} - The rendered component displaying the expanded racecard runner row.
 */
const ExpandedRacecardRunnerRow: FC<Props> = ({
  runner,
  priceFormat = "fractional",
  displaysPremiumData,
  bookmakers,
  refreshListOfTrackedHorses,
  trackedHorse,
}) => {
  const { isSmallAndDown } = useResponsive();
  const [showTracker] = useAtom(showTrackerOnRacecardAtom);
  const [showForm] = useAtom(showFormOnRacecardAtom);
  const [showTimeform] = useAtom(showTimeformOnRacecardAtom);

  const [error, setError] = useState();
  const [formRecords, setFormRecords] = useState<Performance[]>();
  const [formRecordsLoading, setFormRecordsLoading] = useState(true);
  const [showOddsChecker] = useAtom(showOddsCheckerOnRacecardAtom);

  useEffect(() => {
    fetchHorseData();
  }, []);

  /**
   * Fetches the horse form data asynchronously and updates state.
   */
  const fetchHorseData = async () => {
    try {
      const response = await getHorseProfile(runner.horse.slug);
      setFormRecords(response.horse.performances);
    } catch (error) {
      setError(error);
    } finally {
      setFormRecordsLoading(false);
    }
  };

  return (
    <View>
      {showTimeform ? (
        <>
          <View style={[styles.horseDataContainer]}>
            <View style={styles.horseData}>
              <RText color="grey">Col./Sex: </RText>
              <RText weight="semiBold">
                {runner.horse.colors[0]} {runner.horse.gender}
              </RText>
            </View>
            <View style={styles.horseData}>
              <RText color="grey">S: </RText>
              {!!runner.horse.sire_name && (
                <RText weight="semiBold">
                  {runner.horse.sire_name}{" "}
                  {!!runner.horse.country_code?.racing &&
                    runner.horse.country_code?.racing !== "GBR" &&
                    `(${formatCountryCode(runner.horse.country_code?.racing)})`}
                </RText>
              )}
            </View>
            <View style={styles.horseData}>
              <RText color="grey">D: </RText>
              {!!runner.horse.dam_name && (
                <RText weight="semiBold">
                  {runner.horse.dam_name}{" "}
                  {!!runner.horse.country_code?.racing &&
                    runner.horse.country_code?.racing !== "GBR" &&
                    `(${formatCountryCode(runner.horse.country_code?.racing)})`}
                </RText>
              )}
            </View>
          </View>
          <View style={styles.timeFormText}>
            <View
              style={{
                height: 25,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon
                name="timeform"
                style={{
                  width: 54.83,
                  height: 12,
                  alignItems: "center",
                  alignSelf: "center",
                  marginRight: 5,
                }}
                size="xxlarge"
              />
            </View>
            <View
              style={{ marginRight: 5, flexDirection: "row", marginBottom: 2 }}
            >
              {[...Array(5)].map((_, i) => (
                <RText
                  key={i}
                  style={{ marginRight: i === 4 ? 5 : 1 }}
                  color={
                    i + 1 <= runner.timeform_star_rating ? "black" : "grey"
                  }
                >
                  ★
                </RText>
              ))}
            </View>
            <RText>
              {runner.analyst_comment}{" "}
              {displaysPremiumData &&
                runner.icon_badges?.map((badge, i) => (
                  <TimeformIcon badge={badge} key={i} />
                ))}
            </RText>
          </View>
        </>
      ) : null}
      {showForm ? (
        <HorseFormTable
          type="horse"
          form={formRecords}
          loading={formRecordsLoading}
          tableTitle={false}
          expandFormBtn
          limit={6}
          style={{ marginBottom: 0 }}
        />
      ) : null}
      {/* Add this gap only if tracker comment is right under timeform or horse info */}
      {showTracker && !showForm && <Gap vertical size="xsmall" />}
      <TrackedHorseComment
        horseId={runner.horse?.id || runner.id}
        horseName={runner.horse.name}
        isExpanded={showTracker}
        withControls
        refreshListOfTrackedHorses={refreshListOfTrackedHorses}
        trackedHorse={trackedHorse}
      />
      {!!runner?.odds && showOddsChecker && (
        <>
          <Gap vertical size="small" />
          <Odds
            runner={runner}
            priceFormat={priceFormat}
            bookmakers={bookmakers}
          />
        </>
      )}
    </View>
  );
};

/**
 * Displays odds for the given runner.
 * Uses either a grid layout or horizontal scroll view depending on screen size.
 *
 * @component
 * @param {{ runner: Runner; priceFormat: PriceFormat; bookmakers: OddBookmakers }} props
 * @returns {JSX.Element}
 */
const Odds = ({
  runner,
  priceFormat,
  bookmakers,
}: {
  runner: Runner;
  priceFormat: PriceFormat;
  bookmakers: OddBookmakers;
}) => {
  const scrollRef = React.useRef<ScrollView>(null);

  return (
    <View style={{ marginBottom: 10 }}>
      <RScrollView
        scrollRef={scrollRef}
        elementDistance={60 + variables.spacing.small}
        horizontal
        showsHorizontalScrollIndicator={false}
        childrenWidth={60 * runner.odds?.length}
      >
        {sortOddsByPriority(runner.odds, bookmakers).map((odd, i) => {
          return (
            <SquarePriceButton
              key={i}
              odd={odd}
              priceFormat={priceFormat}
              size="large"
              bold
              onPress={() => {}}
              bookmakers={bookmakers}
              style={
                i !== runner.odds.length - 1 && {
                  marginRight: variables.spacing.small,
                }
              }
            />
          );
        })}
      </RScrollView>
    </View>
  );
};

export default ExpandedRacecardRunnerRow;

const { spacing } = variables;
const styles = StyleSheet.create({
  horseDataContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
    justifyContent: "space-between",
  },
  horseData: {
    flexDirection: "row",
    margin: spacing.xxxsmall,
  },
  timeFormText: {
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
    alignItems: "center",
  },
});
